import React, { useEffect, useMemo, useState } from "react";
import {
  CCard,
  CCardBody,
  CRow,
  CCol,
  CWidgetStatsB,
  CFormInput,
  CFormSelect,
  CFormCheck,
  CFormLabel,
  CButton,
  CContainer,
} from "@coreui/react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import ptLocale from "@fullcalendar/core/locales/pt";
import { apiClient } from "services/Api";
import {
  CTab,
  CTabContent,
  CTabList,
  CTabPanel,
  CTabs,
  CBadge,
  CTable,
} from "@coreui/react";
import LoadingScreen from "components/globals/LoadingScreen";
import ErrorPage from "components/globals/ErrorPage";

const tagMapping = {
  "PP Fazer": { label: "Pedido de Pagamento", color: "#cb4335" },
  "PP Pedido de Elementos": {
    label: "Pedido de Elementos (PP)",
    color: "#004D99",
  },
  "Pedido de Elementos": {
    label: "Pedido de Elementos",
    color: "#0a0099",
  },
  "PP Fazer, PP Final": { label: "PTRF", color: "#e67e22" },
  "RCO Arranque": { label: "Fatura de Arranque", color: "#edbf33" },
  "TA: Contratualizar": { label: "Termo de Aceitação", color: "#00bfa0" },
  // "TA: Em Validação": { label: "Termo de Aceitação (Validação)", color: "#16a085" },
  "Pedido de Esclarecimento": {
    label: "Pedido de Esclarecimento",
    color: "#87bc45",
  },
  "AC: Fazer": { label: "Alegações Contrárias", color: "#5d6d7e" },
  "kick-off": { label: "Kick-Off", color: "#633974" },
};

const tableTagMapping = {
  "PP Fazer": { label: "Pedido de Pagamento", color: "#cb4335" },
  "PP Pedido de Elementos": { label: "Pedido de Elementos", color: "#004D99" },
  "RCO Arranque": { label: "Fatura de Arranque", color: "#edbf33" },
  "TA: Contratualizar": { label: "Termo de Aceitação", color: "#00bfa0" },
  "PP Análise Parecer": { label: "Análise Parecer", color: "#16a085" },
  "Pedido de Esclarecimento": {
    label: "Pedido de Esclarecimento",
    color: "#87bc45",
  },
  "AC: Fazer": { label: "Alegações Contrárias", color: "#5d6d7e" },
  "PEDIDO BALCAO: Fazer": { label: "Pedidos Balcão", color: "#5d6d7e" },
  "kick-off": { label: "Kick-Off", color: "#633974" },
};

const parseDueDate = (duedate) => {
  const year = duedate.substring(0, 4);
  const month = duedate.substring(4, 6) - 1; // Mês começa em 0 no JavaScript
  const day = duedate.substring(6, 8);
  return new Date(year, month, day);
};

function formatDate(date) {
  if (!date) return "-";
  const value = String(date);

  if (value.includes("-")) {
    const [year, month, day] = value.substring(0, 10).split("-");
    return `${day}/${month}/${year}`;
  }

  const year = value.substring(0, 4);
  const month = value.substring(4, 6);
  const day = value.substring(6, 8);
  return `${day}/${month}/${year}`;
}

const normalizeText = (value) => (value ? String(value).toLowerCase() : "");

const getTagKey = (task) =>
  task.tags?.length ? task.tags.join(", ") : "Sem Tag";

const getTagInfo = (tagKey) =>
  tagMapping[tagKey] || { label: tagKey, color: "#4f5d73" };

const getDiffDays = (duedate) => {
  if (!duedate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((parseDueDate(duedate) - today) / (1000 * 60 * 60 * 24));
};

const parseDateInput = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const formatRangeLabel = (range) => {
  if (!range?.start || !range?.end) return "Mês corrente + 6 meses";
  const start = range.start.toLocaleDateString("pt-PT");
  const end = new Date(range.end.getTime() - 1).toLocaleDateString("pt-PT");
  return `${start} - ${end}`;
};

const initialFilters = {
  search: "",
  tag: "all",
  status: "all",
  typology: "all",
  dateFrom: "",
  dateTo: "",
  overdueOnly: false,
  hasDateOnly: false,
};

const renderDiffBadge = (item) => {
  if (item.diff === "-" || item.diff === null || item.diff === undefined)
    return "-";
  const value = Number(item.diff);
  if (Number.isNaN(value)) return item.diff;
  const label =
    item.diffType === "elapsed"
      ? `${value}d`
      : value < 0
        ? `${Math.abs(value)}d atraso`
        : value === 0
          ? "Hoje"
          : `${value}d`;
  const color =
    item.diffType === "elapsed"
      ? "info"
      : value < 0
        ? "danger"
        : value <= 7
          ? "warning"
          : "success";
  return (
    <CBadge color={color} className="projects-badge">
      {label}
    </CBadge>
  );
};

const renderCompletionBadge = (completed) => {
  if (completed === null || completed === undefined) return "-";

  return (
    <CBadge
      color={completed ? "success" : "warning"}
      className="projects-badge"
    >
      {completed ? "Concluído" : "A decorrer"}
    </CBadge>
  );
};

const renderMonitoringValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (React.isValidElement(value)) return value;
  if (typeof value === "object") return "-";
  return value;
};

const renderNextMonitoringTask = (monitorizacao) => {
  const task = monitorizacao?.first_incomplete_task;
  const dueDate = task?.due_date || monitorizacao?.first_incomplete_due_date;
  const isOverdue = isOverdueDate(dueDate);

  if (!task?.name && !dueDate) return "-";

  if (!task?.name) {
    return (
      <span className="projects-monitoring-task-date">
        {formatDate(dueDate)}
        {isOverdue && (
          <CBadge color="danger" className="projects-badge ms-2">
            Atrasada
          </CBadge>
        )}
      </span>
    );
  }

  return (
    <span className="projects-monitoring-task">
      <span className="projects-monitoring-task-name">{task.name}</span>
      {dueDate && (
        <span className="projects-monitoring-task-date">
          {formatDate(dueDate)}
          {isOverdue && (
            <CBadge color="danger" className="projects-badge ms-2">
              Atrasada
            </CBadge>
          )}
        </span>
      )}
    </span>
  );
};

const MonitoringMetric = ({ title, rows }) => (
  <div className="projects-monitoring-metric">
    <div className="projects-monitoring-title">{title}</div>
    <div className="projects-monitoring-rows">
      {rows.map((row) => (
        <div
          className={`projects-monitoring-row ${row.className || ""}`}
          key={row.label}
        >
          <span>{row.label}</span>
          <strong>{renderMonitoringValue(row.value)}</strong>
        </div>
      ))}
    </div>
  </div>
);

const isOverdueDate = (dueDate) => {
  if (!dueDate) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return parseDueDate(String(dueDate).replaceAll("-", "")) < today;
};

const IndicatorAssessmentTable = ({ projects }) => {
  if (!projects || projects.length === 0) {
    return (
      <div className="text-medium-emphasis small mb-3">
        Sem dados de avaliação de indicadores.
      </div>
    );
  }

  return (
    <div className="projects-table-wrap projects-monitoring-wrap">
      <table className="table table-striped table-hover align-middle projects-table projects-monitoring-table">
        <thead className="projects-table-head">
          <tr>
            <th>Projeto</th>
            <th>Indicadores de Realização</th>
            <th>1 ano após conclusão</th>
            <th>Ano cruzeiro</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((project) => (
            <tr key={project.id || project.name}>
              <td>
                <div className="fw-semibold">{project.name || "-"}</div>
              </td>
              <td>
                <MonitoringMetric
                  rows={[
                    {
                      label: "Data limite",
                      value: formatDate(
                        project.indicadores_realizacao?.due_date,
                      ),
                    },
                    {
                      label: "Estado",
                      value: renderCompletionBadge(
                        project.indicadores_realizacao?.completed,
                      ),
                    },
                  ]}
                />
              </td>
              <td>
                <MonitoringMetric
                  title="Reporte"
                  rows={[
                    {
                      label: "Data limite",
                      value: formatDate(project.fase_1?.reporte?.due_date),
                    },
                    {
                      label: "Estado",
                      value: renderCompletionBadge(
                        project.fase_1?.reporte?.completed,
                      ),
                    },
                  ]}
                />
                <MonitoringMetric
                  title="Monitorização"
                  rows={[
                    {
                      label: "Início",
                      value: formatDate(
                        project.fase_1?.monitorizacao?.start_date,
                      ),
                    },
                    {
                      label: "Próxima tarefa",
                      value: renderNextMonitoringTask(
                        project.fase_1?.monitorizacao,
                      ),
                      className: "projects-monitoring-row-spaced",
                    },
                  ]}
                />
              </td>
              <td>
                <MonitoringMetric
                  title="Reporte"
                  rows={[
                    {
                      label: "Data limite",
                      value: formatDate(project.fase_2?.reporte?.due_date),
                    },
                    {
                      label: "Estado",
                      value: renderCompletionBadge(
                        project.fase_2?.reporte?.completed,
                      ),
                    },
                  ]}
                />
                <MonitoringMetric
                  title="Monitorização"
                  rows={[
                    {
                      label: "Início",
                      value: formatDate(
                        project.fase_2?.monitorizacao?.start_date,
                      ),
                    },
                    {
                      label: "Próxima tarefa",
                      value: renderNextMonitoringTask(
                        project.fase_2?.monitorizacao,
                      ),
                      className: "projects-monitoring-row-spaced",
                    },
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const TaskTable = ({ items, dateLabel, diffLabel }) => {
  if (!items || items.length === 0) {
    return (
      <div className="text-medium-emphasis small mb-3">
        Sem tarefas para os filtros atuais.
      </div>
    );
  }

  const clickableItems = items.map((item) => ({
    ...item,
    _props: {
      ...item._props,
      className: "projects-table-row",
      onClick: () =>
        window.open(
          `https://streamconsulting.teamwork.com/app/tasks/${item.id}`,
          "_blank",
        ),
    },
  }));

  return (
    <div className="projects-table-wrap">
      <CTable
        className="projects-table"
        columns={[
          { key: "projectNum", label: "#" },
          { key: "company", label: "Empresa" },
          { key: "pp", label: "Tarefa" },
          { key: "projectTypology", label: "Tipologia" },
          { key: "dueDate", label: dateLabel },
          { key: "diff", label: diffLabel },
        ]}
        items={clickableItems}
        tableHeadProps={{ className: "projects-table-head" }}
        striped
        hover
        align="middle"
        scopedColumns={{
          projectNum: (item) => (
            <td>
              <CBadge color="secondary" className="projects-badge">
                {item.projectNum || "-"}
              </CBadge>
            </td>
          ),
          dueDate: (item) => (
            <td className="text-nowrap">{item.dueDate || "-"}</td>
          ),
          diff: (item) => <td>{renderDiffBadge(item)}</td>,
        }}
      />
    </div>
  );
};

const EventCalendar = ({
  tasks,
  statsTasks,
  activeTag,
  onTagSelect,
  onEventHover,
  onEventLeave,
  onRangeChange,
}) => {
  const [tagStats, setTagStats] = useState({});
  const [currentRange, setCurrentRange] = useState(null);

  const firstDayOfCurrentMonth = new Date();
  firstDayOfCurrentMonth.setDate(1); // Primeiro dia do mês corrente

  const firstDayOfTwoMonthsBefore = new Date(firstDayOfCurrentMonth);
  firstDayOfTwoMonthsBefore.setMonth(firstDayOfTwoMonthsBefore.getMonth() - 2);

  const lastDayOfSixMonthsAhead = new Date(firstDayOfCurrentMonth);
  lastDayOfSixMonthsAhead.setMonth(lastDayOfSixMonthsAhead.getMonth() + 6);
  lastDayOfSixMonthsAhead.setDate(0); // Último dia do sexto mês

  useEffect(() => {
    if (!currentRange || !statsTasks) return;

    const filteredTasks = statsTasks.filter((task) => {
      const taskDate = parseDueDate(task.duedate);
      return taskDate >= currentRange.start && taskDate <= currentRange.end;
    });

    const stats = filteredTasks.reduce((acc, task) => {
      const tag = getTagKey(task);
      acc[tag] = acc[tag] || { total: 0, completed: 0 };
      acc[tag].total += 1;
      if (task.completed) acc[tag].completed += 1;
      return acc;
    }, {});

    setTagStats(stats);
  }, [currentRange, statsTasks]);

  const changeDateRange = (info) => {
    const endDate = new Date(info.end);
    endDate.setDate(endDate.getDate() - 1);
    const newRange = { start: info.start, end: endDate };

    if (
      currentRange &&
      JSON.stringify(currentRange) === JSON.stringify(newRange)
    )
      return;

    setCurrentRange(newRange);
    if (onRangeChange) onRangeChange(newRange);
  };

  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const highlightWeekends = ({ date }) => {
    return isWeekend(date) ? "weekend-highlight" : "";
  };

  const highlightPastDates = ({ date }) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date < today) {
      return "past-date";
    }
    return "";
  };

  const events = tasks.map((event) => {
    const tagKey = getTagKey(event);
    const tagInfo = getTagInfo(tagKey);
    const diffDays = getDiffDays(event.duedate);
    const companyShort =
      event.company && event.company.length > 22
        ? `${event.company.slice(0, 22)}...`
        : event.company;
    return {
      id: event.id,
      title: tagInfo.label,
      start: event.duedate,
      allDay: true,
      color: tagInfo.color,
      backgroundColor: tagInfo.color,
      extendedProps: {
        company: event.company,
        companyShort,
        project: event.project,
        projectTypology: event.projectTypology,
        projectNum: event.projectNum,
        completed: event.completed,
        duedate: event.duedate,
        diffDays,
        tagKey,
        tagLabel: tagInfo.label,
        url: `https://streamconsulting.teamwork.com/app/tasks/${event.id}`,
      },
    };
  });

  return (
    <>
      {Object.keys(tagStats).length ? (
        <CRow className="mb-3">
          {Object.entries(tagStats).map(([tag, { total, completed }]) => (
            <CCol key={tag} md={3} lg={2} className="py-1">
              <CWidgetStatsB
                className={`mb-3 projects-tag-card h-100 ${
                  activeTag === tag ? "selected-card" : ""
                }`}
                style={{ backgroundColor: getTagInfo(tag).color }}
                value={total}
                inverse={true}
                progress={{ value: total ? (completed / total) * 100 : 0 }}
                title={getTagInfo(tag).label}
                text={`${completed} concluídos`}
                onClick={() => onTagSelect(activeTag === tag ? "all" : tag)}
              />
            </CCol>
          ))}
        </CRow>
      ) : (
        <div className="projects-empty-tags text-center p-5">
          Sem tarefas previstas para este mês.
        </div>
      )}
      <FullCalendar
        plugins={[dayGridPlugin]}
        initialView="dayGridMonth"
        events={events}
        dayCellClassNames={(args) =>
          `${highlightPastDates(args)} ${highlightWeekends(args)}`
        }
        validRange={{
          start: firstDayOfTwoMonthsBefore,
          end: lastDayOfSixMonthsAhead,
        }}
        datesSet={changeDateRange}
        eventMouseEnter={(info) =>
          onEventHover && onEventHover(info.event.extendedProps, info.jsEvent)
        }
        eventMouseLeave={() => onEventLeave && onEventLeave(null)}
        eventClick={(info) =>
          window.open(info.event.extendedProps.url, "_blank")
        }
        headerToolbar={{ left: "prev", center: "title", right: "next" }}
        dayMaxEventRows={4}
        fixedWeekCount={false}
        showNonCurrentDates={false}
        height="auto"
        locale={ptLocale}
        eventClassNames={(arg) => {
          const classNames = [];
          if (arg.event.extendedProps.completed)
            classNames.push("projects-event-completed");
          if (isOverdueDate(arg.event.extendedProps.duedate))
            classNames.push("projects-event-past");
          return classNames;
        }}
        eventDidMount={(info) => {
          const color = info.event.backgroundColor || info.event.color;
          if (color) info.el.style.setProperty("--event-color", color);
        }}
        eventContent={(arg) => (
          <span
            className={`projects-calendar-event ${
              arg.event.extendedProps.completed ? "is-completed" : ""
            }`}
          >
            <span className="projects-calendar-event-title">
              {arg.event.title}
            </span>
            <span className="projects-calendar-event-subtitle">
              {arg.event.extendedProps.companyShort || "-"}
            </span>
          </span>
        )}
      />
    </>
  );
};

const Projects = () => {
  const [calendarTasks, setCalendarTasks] = useState(null);
  const [tableTasks, setTableTasks] = useState(null);
  const [monitoringProjects, setMonitoringProjects] = useState(null);
  const [error, setError] = useState(false);
  const [calendarRange, setCalendarRange] = useState(null);
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [hoverPosition, setHoverPosition] = useState(null);
  const [filters, setFilters] = useState(initialFilters);

  useEffect(() => {
    apiClient
      .get("/projects/feed/?data=Calendar")
      .then((response) => {
        const tasks = response.data.tasks;
        setCalendarTasks(tasks);
      })
      .catch(() => {
        setError(true);
      });

    apiClient
      .get("/projects/feed/?data=Table")
      .then((response) => {
        const tasks = response.data.tasks;
        const tableTasks = {};

        tasks.forEach((task) => {
          task.tags.forEach((tag) => {
            tableTasks[tag] = tableTasks[tag] || [];

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const projectNum = task.projectNum;
            const company = task.company;
            const projectTypology = task.projectTypology;
            const dueDate = task.duedate;

            switch (tag) {
              case "kick-off":
              case "PEDIDO BALCAO: Fazer":
              case "RCO Arranque":
              case "PP Análise Parecer":
              case "PP Pedido de Elementos":
              case "Pedido de Elementos":
              case "TA: Contratualizar":
              case "AC: Fazer":
              case "PP Fazer":
              case "Pedido de Esclarecimento": {
                const diff = Math.ceil(
                  (parseDueDate(task.duedate) - today) / (1000 * 60 * 60 * 24),
                );

                tableTasks[tag].push({
                  id: task.id,
                  projectNum,
                  company,
                  pp: task.content,
                  projectTypology,
                  dueDate: formatDate(task.duedate),
                  dueDateRaw: task.duedate,
                  diff: dueDate ? diff : "-",
                  diffType: "remaining",
                  _props: {
                    color: !dueDate
                      ? null
                      : diff < 0
                        ? "danger"
                        : diff < 14
                          ? "warning"
                          : null,
                  },
                });
                break;
              }

              case "PEDIDO BALCAO: Em Decisão":
              case "TA: Em Validação":
              case "AC: Decisão":
              case "PP Submetido":
              case "PP Em Pagamento":
              case "PP Em Avaliação": {
                const diff = Math.ceil(
                  (today - parseDueDate(task.duedate)) / (1000 * 60 * 60 * 24),
                );

                tableTasks[tag].push({
                  id: task.id,
                  projectNum,
                  company,
                  pp: task.content,
                  projectTypology,
                  dueDate: formatDate(task.duedate),
                  dueDateRaw: task.duedate,
                  diff: dueDate ? diff : "-",
                  diffType: "elapsed",
                });
                break;
              }
            }
          });
        });

        setTableTasks(tableTasks);
      })
      .catch(() => {
        setError(true);
      });

    apiClient
      .get("/projects/monitoring/")
      .then((response) => {
        setMonitoringProjects(response.data.projects || []);
      })
      .catch(() => {
        setError(true);
      });
  }, []);

  const dateFromValue = useMemo(
    () => parseDateInput(filters.dateFrom),
    [filters.dateFrom],
  );
  const dateToValue = useMemo(() => {
    const parsed = parseDateInput(filters.dateTo);
    if (!parsed) return null;
    parsed.setHours(23, 59, 59, 999);
    return parsed;
  }, [filters.dateTo]);

  const tagOptions = useMemo(() => {
    const set = new Set(Object.keys(tableTagMapping));
    calendarTasks?.forEach((task) => set.add(getTagKey(task)));
    return Array.from(set).sort();
  }, [calendarTasks]);

  const typologyOptions = useMemo(() => {
    const set = new Set();
    calendarTasks?.forEach((task) => {
      if (task.projectTypology) set.add(task.projectTypology);
    });
    if (tableTasks) {
      Object.values(tableTasks).forEach((rows) => {
        rows.forEach((row) => {
          if (row.projectTypology) set.add(row.projectTypology);
        });
      });
    }
    return Array.from(set).sort();
  }, [calendarTasks, tableTasks]);

  const matchesCalendarFilters = (task, { ignoreTag = false } = {}) => {
    const searchTerm = normalizeText(filters.search);
    if (searchTerm) {
      const haystack = [
        task.company,
        task.project,
        task.projectNum,
        task.content,
      ]
        .map(normalizeText)
        .join(" ");
      if (!haystack.includes(searchTerm)) return false;
    }

    const tagKey = getTagKey(task);
    if (!ignoreTag && filters.tag !== "all" && tagKey !== filters.tag)
      return false;

    if (filters.status === "completed" && !task.completed) return false;
    if (filters.status === "pending" && task.completed) return false;

    if (filters.typology !== "all" && task.projectTypology !== filters.typology)
      return false;

    const dueDate = task.duedate ? parseDueDate(task.duedate) : null;
    if ((filters.dateFrom || filters.dateTo) && !dueDate) return false;
    if (filters.hasDateOnly && !dueDate) return false;
    if (dateFromValue && dueDate < dateFromValue) return false;
    if (dateToValue && dueDate > dateToValue) return false;

    if (filters.overdueOnly) {
      const diffDays = getDiffDays(task.duedate);
      if (diffDays === null || diffDays >= 0) return false;
    }

    return true;
  };

  const matchesTableFilters = (item) => {
    if (filters.status === "completed") return false;

    const searchTerm = normalizeText(filters.search);
    if (searchTerm) {
      const haystack = [
        item.company,
        item.projectNum,
        item.pp,
        item.projectTypology,
      ]
        .map(normalizeText)
        .join(" ");
      if (!haystack.includes(searchTerm)) return false;
    }

    if (filters.typology !== "all" && item.projectTypology !== filters.typology)
      return false;

    const dueDate = item.dueDateRaw ? parseDueDate(item.dueDateRaw) : null;
    if ((filters.dateFrom || filters.dateTo) && !dueDate) return false;
    if (filters.hasDateOnly && !dueDate) return false;
    if (dateFromValue && dueDate < dateFromValue) return false;
    if (dateToValue && dueDate > dateToValue) return false;

    if (filters.overdueOnly) {
      if (item.diffType !== "remaining") return false;
      const value = Number(item.diff);
      if (Number.isNaN(value) || value >= 0) return false;
    }

    return true;
  };

  const filteredCalendarTasks = useMemo(() => {
    if (!calendarTasks) return [];
    return calendarTasks.filter((task) => matchesCalendarFilters(task));
  }, [calendarTasks, filters, dateFromValue, dateToValue]);

  const calendarStatsTasks = useMemo(() => {
    if (!calendarTasks) return [];
    return calendarTasks.filter((task) =>
      matchesCalendarFilters(task, { ignoreTag: true }),
    );
  }, [calendarTasks, filters, dateFromValue, dateToValue]);

  const filteredTableTasks = useMemo(() => {
    if (!tableTasks) return {};
    const result = {};
    Object.entries(tableTasks).forEach(([tag, items]) => {
      if (filters.tag !== "all" && filters.tag !== tag) {
        result[tag] = [];
        return;
      }
      result[tag] = items.filter((item) => matchesTableFilters(item));
    });
    return result;
  }, [tableTasks, filters, dateFromValue, dateToValue]);

  const kpiStats = useMemo(() => {
    const total = filteredCalendarTasks.length;
    const completed = filteredCalendarTasks.filter(
      (task) => task.completed,
    ).length;
    const overdue = filteredCalendarTasks.filter((task) => {
      const diff = getDiffDays(task.duedate);
      return diff !== null && diff < 0;
    }).length;
    const due7 = filteredCalendarTasks.filter((task) => {
      const diff = getDiffDays(task.duedate);
      return diff !== null && diff >= 0 && diff <= 7;
    }).length;
    const due30 = filteredCalendarTasks.filter((task) => {
      const diff = getDiffDays(task.duedate);
      return diff !== null && diff >= 0 && diff <= 30;
    }).length;
    const companies = new Set(
      filteredCalendarTasks.map((task) => task.company).filter(Boolean),
    ).size;
    const projects = new Set(
      filteredCalendarTasks.map((task) => task.project).filter(Boolean),
    ).size;
    const completionRate = total ? Math.round((completed / total) * 100) : 0;

    return {
      total,
      completed,
      pending: total - completed,
      overdue,
      due7,
      due30,
      companies,
      projects,
      completionRate,
    };
  }, [filteredCalendarTasks]);

  const activeFilters = useMemo(() => {
    const entries = [];
    if (filters.search) entries.push(`Pesquisa: ${filters.search}`);
    if (filters.tag !== "all")
      entries.push(`Tipo: ${getTagInfo(filters.tag).label}`);
    if (filters.status !== "all")
      entries.push(
        `Estado: ${filters.status === "completed" ? "Concluídas" : "Pendentes"}`,
      );
    if (filters.typology !== "all")
      entries.push(`Tipologia: ${filters.typology}`);
    if (filters.dateFrom) entries.push(`Desde: ${filters.dateFrom}`);
    if (filters.dateTo) entries.push(`Até: ${filters.dateTo}`);
    if (filters.overdueOnly) entries.push("Só atrasadas");
    if (filters.hasDateOnly) entries.push("Só com data");
    return entries;
  }, [filters]);

  const calendarRangeLabel = formatRangeLabel(calendarRange);

  if (error) return <ErrorPage message="Erro ao carregar dados da Teamwork" />;

  if (!calendarTasks || !tableTasks || !monitoringProjects)
    return <LoadingScreen message="A carregar dados da Teamwork ..." />;

  return (
    <CContainer>
      <CRow className="g-3 mb-4 align-items-stretch">
        <CCol sm={6} lg={4} xl={2}>
          <CWidgetStatsB
            className="projects-kpi-card h-100 kpi-total"
            value={kpiStats.total}
            title="Tarefas no período"
            text={`Empresas: ${kpiStats.companies} | Projetos: ${kpiStats.projects}`}
          />
        </CCol>
        <CCol sm={6} lg={4} xl={2}>
          <CWidgetStatsB
            className="projects-kpi-card h-100 kpi-pending"
            value={kpiStats.pending}
            title="Pendentes"
            text="A acompanhar"
          />
        </CCol>
        <CCol sm={6} lg={4} xl={2}>
          <CWidgetStatsB
            className="projects-kpi-card h-100 kpi-completion"
            value={`${kpiStats.completionRate}%`}
            title="Conclusão"
            text={`${kpiStats.completed} concluída(s)`}
            progress={{ value: kpiStats.completionRate }}
          />
        </CCol>
        <CCol sm={6} lg={4} xl={2}>
          <CWidgetStatsB
            className="projects-kpi-card h-100 kpi-overdue"
            value={kpiStats.overdue}
            title="Atrasadas"
            text="Prioridade alta"
          />
        </CCol>
        <CCol sm={6} lg={4} xl={2}>
          <CWidgetStatsB
            className="projects-kpi-card h-100 kpi-due7"
            value={kpiStats.due7}
            title="Prazo 7 dias"
            text="Curto prazo"
          />
        </CCol>
        <CCol sm={6} lg={4} xl={2}>
          <CWidgetStatsB
            className="projects-kpi-card h-100 kpi-due30"
            value={kpiStats.due30}
            title="Prazo 30 dias"
            text="Médio prazo"
          />
        </CCol>
      </CRow>

      <div className="management-filters mb-4">
        <div className="management-filters-header">
          <div className="management-section-title">Filtros</div>
          <CButton
            color="light"
            size="sm"
            onClick={() => setFilters(initialFilters)}
          >
            Limpar filtros
          </CButton>
        </div>
        <CRow className="g-3 management-filters-grid">
          <CCol xs={12} md={6} lg={4}>
            <CFormLabel>Pesquisar</CFormLabel>
            <CFormInput
              value={filters.search}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  search: event.target.value,
                }))
              }
              placeholder="Empresa, projeto, tipologia, tarefa"
            />
          </CCol>
          <CCol xs={12} md={6} lg={3}>
            <CFormLabel>Tipo</CFormLabel>
            <CFormSelect
              value={filters.tag}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  tag: event.target.value,
                }))
              }
            >
              <option value="all">Todos os tipos</option>
              {tagOptions.map((tag) => (
                <option key={tag} value={tag}>
                  {getTagInfo(tag).label}
                </option>
              ))}
            </CFormSelect>
          </CCol>
          <CCol xs={12} md={6} lg={2}>
            <CFormLabel>Estado</CFormLabel>
            <CFormSelect
              value={filters.status}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  status: event.target.value,
                }))
              }
            >
              <option value="all">Todos</option>
              <option value="pending">Pendentes</option>
              <option value="completed">Concluídas</option>
            </CFormSelect>
          </CCol>
          <CCol xs={12} md={6} lg={3}>
            <CFormLabel>Tipologia</CFormLabel>
            <CFormSelect
              value={filters.typology}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  typology: event.target.value,
                }))
              }
            >
              <option value="all">Todas</option>
              {typologyOptions.map((typology) => (
                <option key={typology} value={typology}>
                  {typology}
                </option>
              ))}
            </CFormSelect>
          </CCol>
          <CCol xs={12} md={6} lg={2}>
            <CFormLabel>Desde</CFormLabel>
            <CFormInput
              type="date"
              value={filters.dateFrom}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  dateFrom: event.target.value,
                }))
              }
            />
          </CCol>
          <CCol xs={12} md={6} lg={2}>
            <CFormLabel>Até</CFormLabel>
            <CFormInput
              type="date"
              value={filters.dateTo}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  dateTo: event.target.value,
                }))
              }
            />
          </CCol>
          <CCol xs={12} md={6} lg={2} className="management-filter-check">
            <CFormCheck
              label="Só atrasadas"
              checked={filters.overdueOnly}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  overdueOnly: event.target.checked,
                }))
              }
            />
          </CCol>
          <CCol xs={12} md={6} lg={2} className="management-filter-check">
            <CFormCheck
              label="Só com data"
              checked={filters.hasDateOnly}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  hasDateOnly: event.target.checked,
                }))
              }
            />
          </CCol>
        </CRow>
        <div className="management-active-filters">
          {activeFilters.length ? (
            activeFilters.map((filter) => (
              <CBadge key={filter} color="info" className="projects-badge">
                {filter}
              </CBadge>
            ))
          ) : (
            <span className="text-medium-emphasis small">
              Sem filtros aplicados.
            </span>
          )}
        </div>
      </div>

      <CRow className="g-3 mb-4">
        <CCol xs={12}>
          <div className="projects-calendar-card">
            <EventCalendar
              tasks={filteredCalendarTasks}
              statsTasks={calendarStatsTasks}
              activeTag={filters.tag}
              onTagSelect={(tag) => setFilters((prev) => ({ ...prev, tag }))}
              onEventHover={(eventData, jsEvent) => {
                setHoveredEvent(eventData);
                if (jsEvent) {
                  setHoverPosition({
                    x: jsEvent.clientX,
                    y: jsEvent.clientY,
                  });
                }
              }}
              onEventLeave={() => {
                setHoveredEvent(null);
                setHoverPosition(null);
              }}
              onRangeChange={setCalendarRange}
            />
            {hoveredEvent && hoverPosition ? (
              <div
                className="projects-hover-float"
                style={{
                  left: hoverPosition.x + 16,
                  top: hoverPosition.y + 16,
                }}
              >
                <div className="projects-section-title">Detalhe rápido</div>
                <div className="projects-hover-details">
                  <div className="projects-hover-tag">
                    <CBadge
                      className="projects-badge"
                      style={{
                        backgroundColor: getTagInfo(hoveredEvent.tagKey).color,
                        color: "#fff",
                      }}
                    >
                      {hoveredEvent.tagLabel}
                    </CBadge>
                  </div>
                  <div className="projects-hover-title">
                    {hoveredEvent.project || "-"}
                  </div>
                  <div className="text-medium-emphasis">
                    {hoveredEvent.company || "-"}
                  </div>
                  {hoveredEvent.projectNum ? (
                    <div className="projects-hover-meta">
                      Projeto #: {hoveredEvent.projectNum}
                    </div>
                  ) : null}
                  {hoveredEvent.projectTypology ? (
                    <div className="projects-hover-meta">
                      Tipologia: {hoveredEvent.projectTypology}
                    </div>
                  ) : null}
                  {hoveredEvent.duedate ? (
                    <div className="projects-hover-meta">
                      Data limite: {formatDate(hoveredEvent.duedate)}
                    </div>
                  ) : null}
                  {hoveredEvent.diffDays !== null ? (
                    <div className="projects-hover-meta">
                      Prazo:{" "}
                      {hoveredEvent.diffDays < 0
                        ? `${Math.abs(hoveredEvent.diffDays)}d atraso`
                        : hoveredEvent.diffDays === 0
                          ? "Hoje"
                          : `${hoveredEvent.diffDays}d`}
                    </div>
                  ) : null}
                  <div className="projects-hover-meta">
                    Estado:{" "}
                    <CBadge
                      color={hoveredEvent.completed ? "success" : "warning"}
                      className="projects-badge"
                    >
                      {hoveredEvent.completed ? "Concluída" : "Pendente"}
                    </CBadge>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </CCol>
      </CRow>

      <CCard>
        <CCardBody>
          <CTabs activeItemKey={Object.keys(tableTagMapping)[0]}>
            <CTabList variant="underline-border" layout="fill">
              {Object.keys(tableTagMapping).map((tag) => (
                <CTab key={tag} aria-controls="home-tab-pane" itemKey={tag}>
                  <span className="position-relative px-4">
                    {tableTagMapping[tag].label}
                    <CBadge
                      style={{ backgroundColor: tableTagMapping[tag].color }}
                      position="top-end"
                      shape="rounded-pill"
                    >
                      {filteredTableTasks[tag]?.length ?? 0}
                    </CBadge>
                  </span>
                </CTab>
              ))}
              <CTab itemKey="avaliacao-indicadores">
                <span className="position-relative px-4">
                  Avaliação Indicadores
                  <CBadge color="info" position="top-end" shape="rounded-pill">
                    {monitoringProjects.length}
                  </CBadge>
                </span>
              </CTab>
            </CTabList>
            <CTabContent>
              <CTabPanel className="py-3" itemKey={"PP Fazer"}>
                <div className="projects-table-section">
                  <div className="projects-section-title">
                    Pedido de Pagamento
                  </div>
                  <TaskTable
                    items={filteredTableTasks["PP Fazer"] || []}
                    dateLabel="Data Limite"
                    diffLabel="Prazo"
                  />
                </div>
                <div className="projects-table-section">
                  <div className="projects-section-title">Submetido</div>
                  <TaskTable
                    items={filteredTableTasks["PP Submetido"] || []}
                    dateLabel="Data Submissão"
                    diffLabel="Duração"
                  />
                </div>
                <div className="projects-table-section">
                  <div className="projects-section-title">Em avaliação</div>
                  <TaskTable
                    items={filteredTableTasks["PP Em Avaliação"] || []}
                    dateLabel="Data Submissão"
                    diffLabel="Duração"
                  />
                </div>
                <div className="projects-table-section">
                  <div className="projects-section-title">Em pagamento</div>
                  <TaskTable
                    items={filteredTableTasks["PP Em Pagamento"] || []}
                    dateLabel="Data Parecer"
                    diffLabel="Duração"
                  />
                </div>
              </CTabPanel>
              <CTabPanel className="py-3" itemKey={"PP Pedido de Elementos"}>
                <div className="projects-table-section">
                  <div className="projects-section-title">
                    Pedido de Elementos (PP)
                  </div>
                  <TaskTable
                    items={filteredTableTasks["PP Pedido de Elementos"] || []}
                    dateLabel="Data Limite"
                    diffLabel="Prazo"
                  />
                  <div className="projects-section-title">
                    Pedido de Elementos
                  </div>
                  <TaskTable
                    items={filteredTableTasks["Pedido de Elementos"] || []}
                    dateLabel="Data Limite"
                    diffLabel="Prazo"
                  />
                </div>
              </CTabPanel>
              <CTabPanel className="py-3" itemKey={"Pedido de Esclarecimento"}>
                <div className="projects-table-section">
                  <div className="projects-section-title">
                    Pedido de Esclarecimento
                  </div>
                  <TaskTable
                    items={filteredTableTasks["Pedido de Esclarecimento"] || []}
                    dateLabel="Data Limite"
                    diffLabel="Prazo"
                  />
                </div>
              </CTabPanel>
              <CTabPanel className="py-3" itemKey={"TA: Contratualizar"}>
                <div className="projects-table-section">
                  <div className="projects-section-title">
                    Termo de Aceitação
                  </div>
                  <TaskTable
                    items={filteredTableTasks["TA: Contratualizar"] || []}
                    dateLabel="Data Limite"
                    diffLabel="Prazo"
                  />
                </div>
                <div className="projects-table-section">
                  <div className="projects-section-title">Em validação</div>
                  <TaskTable
                    items={filteredTableTasks["TA: Em Validação"] || []}
                    dateLabel="Data Limite"
                    diffLabel="Prazo"
                  />
                </div>
              </CTabPanel>
              <CTabPanel className="py-3" itemKey={"AC: Fazer"}>
                <div className="projects-table-section">
                  <div className="projects-section-title">
                    Alegações Contrárias
                  </div>
                  <TaskTable
                    items={filteredTableTasks["AC: Fazer"] || []}
                    dateLabel="Data Limite"
                    diffLabel="Prazo"
                  />
                </div>
                <div className="projects-table-section">
                  <div className="projects-section-title">Decisão</div>
                  <TaskTable
                    items={filteredTableTasks["AC: Decisão"] || []}
                    dateLabel="Data Submissão"
                    diffLabel="Duração"
                  />
                </div>
              </CTabPanel>
              <CTabPanel className="py-3" itemKey={"PP Análise Parecer"}>
                <div className="projects-table-section">
                  <div className="projects-section-title">
                    Análise de Parecer
                  </div>
                  <TaskTable
                    items={filteredTableTasks["PP Análise Parecer"] || []}
                    dateLabel="Data Limite"
                    diffLabel="Prazo"
                  />
                </div>
              </CTabPanel>
              <CTabPanel className="py-3" itemKey={"RCO Arranque"}>
                <div className="projects-table-section">
                  <div className="projects-section-title">
                    Fatura de Arranque
                  </div>
                  <TaskTable
                    items={filteredTableTasks["RCO Arranque"] || []}
                    dateLabel="Data Limite"
                    diffLabel="Prazo"
                  />
                </div>
              </CTabPanel>
              <CTabPanel className="py-3" itemKey={"PEDIDO BALCAO: Fazer"}>
                <div className="projects-table-section">
                  <div className="projects-section-title">Pedidos Balcão</div>
                  <TaskTable
                    items={filteredTableTasks["PEDIDO BALCAO: Fazer"] || []}
                    dateLabel="Data Limite"
                    diffLabel="Prazo"
                  />
                </div>
                <div className="projects-table-section">
                  <div className="projects-section-title">Em decisão</div>
                  <TaskTable
                    items={
                      filteredTableTasks["PEDIDO BALCAO: Em Decisão"] || []
                    }
                    dateLabel="Data Submissão"
                    diffLabel="Duração"
                  />
                </div>
              </CTabPanel>
              <CTabPanel className="py-3" itemKey={"kick-off"}>
                <div className="projects-table-section">
                  <div className="projects-section-title">Kick-off</div>
                  <TaskTable
                    items={filteredTableTasks["kick-off"] || []}
                    dateLabel="Data Limite"
                    diffLabel="Prazo"
                  />
                </div>
              </CTabPanel>
              <CTabPanel className="py-3" itemKey="avaliacao-indicadores">
                <div className="projects-table-section">
                  <div className="projects-section-title">
                    Avaliação Indicadores
                  </div>
                  <IndicatorAssessmentTable projects={monitoringProjects} />
                </div>
              </CTabPanel>
            </CTabContent>
          </CTabs>
        </CCardBody>
      </CCard>
    </CContainer>
  );
};

export default Projects;
